import React, { useState, useEffect } from 'react';
import { CACHE_KEYS, TOKEN_KEYS, findAnyStoredToken, useAuth } from './contexts/AuthContext';
import AuthSelector from './components/AuthSelector';
import VKAuth from './components/VKAuth';
import TelegramAuth from './components/TelegramAuth';
import MaxAuth from './components/MaxAuth';
import YandexAuth from './components/YandexAuth';
import EmailAuth from './components/EmailAuth';
import TelegramConnect from './components/TelegramConnect';
import VKConnect from './components/VKConnect';
import ConnectAccountConfirm from './components/ConnectAccountConfirm';
import ConnectSuccess from './components/ConnectSuccess';
import AccountLinkingPage from './components/AccountLinkingPage';
import DebugLogger from './components/DebugLogger';
import OAuthConsent from './components/OAuthConsent';
import { TokenStatus, ConnectionTokenData, ConnectAccountResponse, BackendUser, AuthPlatform, StoredTokens } from './types';
import { isMobileUserAgent } from './utils/mobileUtils';
import { authLogger } from './utils/logger';
import { saveServiceFromUrl } from './utils/serviceUtils';
import { normalizeReturnUrl } from './utils/returnUrl';
import { listVkAccounts, removeVkAccount, setActiveVkAccount, type StoredVkAccount } from './utils/vkAccounts';
import { sessionGet, sessionRemove, sessionSet, storageGet, storageRemove } from './utils/storage';
import { readYandexCallback } from './utils/yandex';
import { getSkin, skinLoginUrl, skinOrigin } from './utils/skin';

type AuthMode = 'selector' | 'vk' | 'telegram' | 'max' | 'yandex' | 'email' | 'telegram-connect' | 'vk-connect' | 'connect-confirm' | 'connect-success' | 'account-linking' | 'account-linking-login-vk' | 'account-linking-login-telegram' | 'account-linking-login-max' | 'account-linking-login-email' | 'link';

const PLATFORM_LABELS: Record<AuthPlatform, string> = {
  vk: 'ВКонтакте',
  telegram: 'Telegram',
  max: 'MAX',
  yandex: 'Яндекс ID',
  email: 'Почта',
};

/**
 * Флаги режима связывания живут в sessionStorage, потому что VK ID уводит на
 * id.vk.ru и возвращает нас на голый origin — `?mode=account-linking` и выбор
 * главного аккаунта до этого не доживали, и после входа во ВКонтакте юзера
 * выбрасывало обратно в сервис по return_url. Оттуда сервис снова слал его
 * связывать аккаунты — тот самый круг.
 */
const LINKING_MODE_KEY = 'ssmm-linking-mode';
const LINKING_PRIMARY_KEY = 'ssmm-linking-primary';
/**
 * Подсказки сервиса, открывшего связывание: `uid` — его аккаунт, `connect` — что к нему
 * подключить (`?mode=account-linking&uid=…&connect=vk`). Тоже переживают id.vk.ru.
 */
const LINKING_UID_KEY = 'ssmm-linking-uid';
const LINKING_CONNECT_KEY = 'ssmm-linking-connect';

/** Главным аккаунтом может быть любая сессия — в т.ч. заведённая по коду с почты. */
type LinkingProvider = AuthPlatform;
/** А прицепить к нему можно только то, у чего есть ручка connect. */
type LinkableProvider = 'vk' | 'telegram';

const LINKING_PRIMARIES: LinkingProvider[] = ['vk', 'telegram', 'max', 'yandex', 'email'];
const LINKABLE: LinkableProvider[] = ['vk', 'telegram'];

const readLinkingPrimary = (): LinkingProvider | null => {
  const stored = sessionGet(LINKING_PRIMARY_KEY) as LinkingProvider | null;
  return stored && LINKING_PRIMARIES.includes(stored) ? stored : null;
};

const saveLinkingHints = (urlParams: URLSearchParams) => {
  const uid = urlParams.get('uid')?.trim();
  const connect = urlParams.get('connect') as LinkableProvider | null;
  if (uid) sessionSet(LINKING_UID_KEY, uid);
  else sessionRemove(LINKING_UID_KEY);
  if (connect && LINKABLE.includes(connect)) sessionSet(LINKING_CONNECT_KEY, connect);
  else sessionRemove(LINKING_CONNECT_KEY);
};

const clearLinkingHints = () => {
  sessionRemove(LINKING_UID_KEY);
  sessionRemove(LINKING_CONNECT_KEY);
};

/** Сессия паспорта, принадлежащая аккаунту `uid`. */
const findPlatformByUid = (tokens: StoredTokens, uid: string): LinkingProvider | null =>
  LINKING_PRIMARIES.find((platform) => {
    const data = tokens[platform];
    return Boolean(data) && !data?.isExpired && data?.backendUser?.uid === uid;
  }) ?? null;

// Читаем сохранённого пользователя из кэша localStorage (синхронно, до опроса бэка),
// чтобы кнопки провайдеров сразу показывали аккаунт, а не пустой стейт во время загрузки.
const readCachedTokenStatus = (platform: AuthPlatform): TokenStatus | undefined => {
  try {
    if (!storageGet(TOKEN_KEYS[platform])) return undefined;
    const raw = storageGet(CACHE_KEYS[platform]);
    if (!raw) return undefined;
    const { user, backendUser } = JSON.parse(raw);
    if (!user || !backendUser) return undefined;
    return { user, backendUser, isExpired: false };
  } catch {
    return undefined;
  }
};

const readCachedTokensData = (): StoredTokens => ({
  vk: readCachedTokenStatus('vk'),
  telegram: readCachedTokenStatus('telegram'),
  max: readCachedTokenStatus('max'),
  yandex: readCachedTokenStatus('yandex'),
  email: readCachedTokenStatus('email'),
});

const App: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('selector');
  // Сидируем из кэша: аккаунты видны сразу, пока идёт проверка на бэке.
  const [storedTokensData, setStoredTokensData] = useState<StoredTokens>(() => readCachedTokensData());
  // true, пока checkStoredTokens опрашивает бэк по сохранённым сессиям.
  const [sessionsVerifying, setSessionsVerifying] = useState<boolean>(false);
  const [connectionToken, setConnectionToken] = useState<string>('');
  const [connectionData, setConnectionData] = useState<ConnectionTokenData | null>(null);
  const [connectResult, setConnectResult] = useState<ConnectAccountResponse | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [vkAccounts, setVkAccounts] = useState<StoredVkAccount[]>(() => listVkAccounts());
  // Вход через Яндекс начали из экрана «Почта» — влияет только на кнопку «Назад».
  const [yandexFromMail, setYandexFromMail] = useState(false);
  // Вход ВКонтакте другой страницей (см. VKAuth.newAccount).
  const [vkNewAccount, setVkNewAccount] = useState(false);
  // Главный аккаунт в режиме связывания: к нему прицепляется второй провайдер.
  // Переживает редирект на id.vk.ru через sessionStorage.
  const [linkingPrimary, setLinkingPrimary] = useState<LinkingProvider | null>(() => readLinkingPrimary());
  const { state, checkStoredTokens, sendToParent, redirectToReturnUrl, login, clearStoredData, connectAccount } = useAuth();

  // Определяем параметры из URL и проверяем токены
  useEffect(() => {
    // Логируем старт приложения
    authLogger.info('Приложение запущено');
    
    // Сохраняем service из URL в sessionStorage
    saveServiceFromUrl();
    
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode') as AuthMode;
    const payload = urlParams.get('payload'); // Проверяем callback от VK ID (старый silent_token формат)
    const vkCode = urlParams.get('code'); // Проверяем callback от VK ID (новый OAuth 2.1 формат)
    const vkDeviceId = urlParams.get('device_id');
    const vkState = urlParams.get('state');
    const returnUrl = urlParams.get('return_url');
    const merge = urlParams.get('merge'); // Режим объединения аккаунтов
    const from = urlParams.get('from'); // Платформа для подключения (telegram/vk)
    const service = urlParams.get('service'); // Сервис для заголовка service-name

    // Если есть VK ID OAuth 2.1 callback (code+device_id+state), нужно понять,
    // это VK Auth или VK Connect — определяем по содержимому state.
    const isVKv2Callback = Boolean(vkCode && vkDeviceId && vkState);
    let vkv2Mode: 'vk' | 'vk-connect' | null = null;
    if (isVKv2Callback && vkState) {
      try {
        const normalized = vkState
          .trim()
          .replace(/ /g, '+')
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .replace(/=+$/g, '');
        const padLen = (4 - (normalized.length % 4)) % 4;
        const stateData = JSON.parse(atob(`${normalized}${'='.repeat(padLen)}`));
        vkv2Mode = stateData?.mode === 'connect' ? 'vk-connect' : 'vk';
      } catch {
        vkv2Mode = 'vk';
      }
    }

    // Возврат от Яндекса/шлюза распознаём здесь же: ниже он нужен и как «мы не с нуля
    // открылись», и как отдельная ветка маршрутизации.
    const yandexCallback = readYandexCallback();

    // Режим связывания запоминаем на всю сессию вкладки. Провайдерские редиректы
    // (VK ID, Яндекс) возвращают нас без исходного query, поэтому по одному только
    // `?mode=` понять, что мы всё ещё связываем аккаунты, невозможно. И наоборот:
    // обычный вход, открытый заново, флаг обязан сбросить, иначе вкладка залипнет
    // в связывании.
    const isProviderCallback = isVKv2Callback || Boolean(payload) || Boolean(yandexCallback);
    if (mode === 'link' || mode === 'account-linking') {
      sessionSet(LINKING_MODE_KEY, 'true');
      saveLinkingHints(urlParams);
    } else if (!isProviderCallback) {
      sessionRemove(LINKING_MODE_KEY);
      sessionRemove(LINKING_PRIMARY_KEY);
      clearLinkingHints();
      setLinkingPrimary(null);
    }

    authLogger.info('URL параметры', {
      mode,
      hasPayload: !!payload,
      isVKv2Callback,
      vkv2Mode,
      returnUrl,
      merge,
      from,
      service,
      fullUrl: window.location.href
    });
    
    // Сохраняем return_url в sessionStorage, если он есть (снимаем лишнее кодирование)
    if (returnUrl) {
      const stored = normalizeReturnUrl(returnUrl) ?? returnUrl;
      sessionSet('ssmm-return-url', stored);
      authLogger.info('return_url сохранен в sessionStorage', { returnUrl: stored });
    }
    
    // Возврат от Яндекса/шлюза — в адресе лежит токен, его надо обработать раньше всего
    if (yandexCallback) {
      authLogger.info('Обнаружен возврат от Яндекс ID', {
        mode: yandexCallback.mode,
        hasToken: Boolean(yandexCallback.ssmmToken || yandexCallback.yandexToken),
        hasError: Boolean(yandexCallback.error),
      });
      setAuthMode('yandex');
      return;
    }

    // Проверяем режим связывания аккаунтов (отдельная страница)
    if (mode === 'link' || mode === 'account-linking') {
      authLogger.info('Режим связывания аккаунтов');
      // Сначала проверяем сохраненные токены
      const checkTokensAndSetMode = async () => {
        const tokensData = await checkStoredTokens();
        if (tokensData) {
          setStoredTokensData(tokensData);
        }
        if (await followLinkingHints(tokensData)) return;
        // Повторный прогон эффекта (StrictMode, новая checkStoredTokens) подсказку уже не
        // видит — она одноразовая — и не должен выдёргивать с экрана подключения.
        setAuthMode((prev) => (prev === 'telegram-connect' || prev === 'vk-connect' ? prev : 'account-linking'));
      };
      checkTokensAndSetMode();
      return;
    }
    
    // Проверяем режим объединения аккаунтов
    if (merge === 'true' && from) {
      if (from === 'telegram') {
        authLogger.info('Режим объединения: подключение Telegram');
        setAuthMode('telegram-connect');
        return;
      }
      if (from === 'vk') {
        authLogger.info('Режим объединения: подключение VK');
        setAuthMode('vk-connect');
        return;
      }
    }
    
    if ((mode as string) === 'vk-add') {
      // Сервис добавляет ещё одну страницу: сразу вход ВКонтакте другой страницей, без списка
      // сохранённых сессий и без «Продолжить как …» (тестировщики socpower застревали на этих шагах).
      setVkNewAccount(true);
      setAuthMode('vk');
    } else if (mode && ['vk', 'telegram', 'max', 'yandex', 'email'].includes(mode)) {
      // Если есть принудительный режим из URL, используем его
      setAuthMode(mode);
    } else if (payload) {
      // Если есть payload от VK ID (старый silent_token формат), показываем VK
      setAuthMode('vk');
    } else if (isVKv2Callback && vkv2Mode) {
      // VK ID OAuth 2.1 callback (code+device_id+state) после редиректа от id.vk.ru —
      // маршрутим на нужный компонент, который обработает обмен кода на токен.
      authLogger.info('Обнаружен VK ID OAuth 2.1 callback, маршрутизация', { vkv2Mode });
      setAuthMode(vkv2Mode);
    } else {
      // Если нет принудительного режима из URL, проверяем сохраненные токены
      const checkTokens = async () => {
        setSessionsVerifying(true);
        try {
          const tokensData = await checkStoredTokens();
          if (tokensData) {
            setStoredTokensData(tokensData);
          }
          setVkAccounts(listVkAccounts());
        } finally {
          setSessionsVerifying(false);
        }
      };

      checkTokens();
    }
  }, [checkStoredTokens]);

  // Экран согласия сервера авторизации: отдельный маршрут по пути (не по mode),
  // чтобы не смешиваться с legacy-экранами входа/связывания ниже.
  // Проверка стоит после useEffect выше (а не перед ним), иначе при логине на этой
  // же странице state.token сменится с '' на токен и компонент между рендерами
  // вызовет разное число хуков — React выбросит ошибку порядка хуков.
  // Пока пользователь не залогинен, ветка не срабатывает, и ниже отрисуется обычный вход.
  const isConsentRoute = window.location.pathname === '/oauth/consent';
  const consentRequestId = new URLSearchParams(window.location.search).get('request_id');
  if (isConsentRoute && consentRequestId && state.token) {
    return <OAuthConsent requestId={consentRequestId} token={state.token} />;
  }

  const handleSelectVK = () => {
    setVkNewAccount(false);
    setAuthMode('vk');
  };

  // Завершение авторизации: extension → токен в URL, мобила+return_url → редирект,
  // иначе postMessage родителю. Едина для входа/быстрого входа/смены аккаунта.
  const completeAuth = (token: string, backendUser: BackendUser) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');
    const isMobile = isMobileUserAgent();
    const mode = urlParams.get('mode');

    if (mode === 'extension') {
      redirectToReturnUrl(token);
    } else if (returnUrl && isMobile) {
      redirectToReturnUrl(token);
    } else {
      sendToParent({ type: 'AUTH_SUCCESS', data: { token, user: backendUser } });
    }
  };

  // «Добавить аккаунт ВКонтакте» — вход другой страницей: виджет без «Продолжить как <текущая>»,
  // VK ID сразу просит войти. По успеху новый аккаунт попадёт в список и станет активным.
  const handleAddVkAccount = () => {
    setVkNewAccount(true);
    setAuthMode('vk');
  };

  // Быстрое переключение на ранее сохранённый VK-аккаунт.
  const handleSwitchVkAccount = (acc: StoredVkAccount) => {
    authLogger.info('Переключение VK-аккаунта', { uid: acc.uid });
    setActiveVkAccount(acc.uid);
    login(acc.user, acc.token, acc.backendUser);
    completeAuth(acc.token, acc.backendUser);
  };

  // Удаление сохранённого VK-аккаунта из списка; активный — ещё и из активной сессии.
  const handleRemoveVkAccount = (acc: StoredVkAccount) => {
    const activeUid = storedTokensData.vk
      ? String(storedTokensData.vk.backendUser?.uid || storedTokensData.vk.backendUser?.vkId || storedTokensData.vk.user.id)
      : null;
    setVkAccounts(removeVkAccount(acc.uid));
    if (acc.uid === activeUid) {
      clearStoredData('vk');
      setStoredTokensData((prev) => ({ ...prev, vk: undefined }));
    }
  };

  const handleSelectTelegram = () => {
    setAuthMode('telegram');
  };

  const handleSelectMax = () => {
    setAuthMode('max');
  };

  const handleSelectYandex = () => {
    setYandexFromMail(false);
    setAuthMode('yandex');
  };

  // Яндекс, выбранный внутри «Почты»: «Назад» должно вернуть в тот же выбор способа.
  const handleSelectYandexFromMail = () => {
    setYandexFromMail(true);
    setAuthMode('yandex');
  };

  const handleBackFromYandex = () => {
    if (yandexFromMail) {
      setYandexFromMail(false);
      setAuthMode('email');
      return;
    }
    void handleBackToSelector();
  };

  const handleSelectEmail = () => {
    setAuthMode('email');
  };

  const handleConnectTelegram = () => {
    setAuthMode('telegram-connect');
  };

  // Обработчики для AccountLinkingPage
  const handleSelectPrimaryAccount = (provider: LinkingProvider) => {
    authLogger.info('Выбран главный аккаунт', { provider });
    // Выбор должен дожить до подтверждения связывания — в т.ч. через редирект
    // на id.vk.ru, поэтому дублируем его в sessionStorage.
    setLinkingPrimary(provider);
    sessionSet(LINKING_PRIMARY_KEY, provider);
  };

  const handleConnectProviderFromLinking = (provider: LinkableProvider) => {
    authLogger.info('Подключение провайдера из страницы связывания', { provider });
    sessionSet(LINKING_MODE_KEY, 'true');

    if (provider === 'telegram') {
      setAuthMode('telegram-connect');
    } else if (provider === 'vk') {
      setAuthMode('vk-connect');
    }
  };

  const handleLoginProviderFromLinking = async (provider: LinkingProvider) => {
    authLogger.info('Авторизация провайдера из страницы связывания', { provider });

    // Сохраняем, что мы в режиме связывания
    sessionSet(LINKING_MODE_KEY, 'true');

    if (provider === 'vk') {
      setAuthMode('account-linking-login-vk');
    } else if (provider === 'telegram') {
      setAuthMode('account-linking-login-telegram');
    } else if (provider === 'max') {
      setAuthMode('account-linking-login-max');
    } else if (provider === 'email' || provider === 'yandex') {
      // Вход по коду с почты (и Яндекс внутри него) — такой же законный способ
      // получить главный аккаунт, к которому потом цепляется Telegram.
      setAuthMode('account-linking-login-email');
    }
  };

  const handleBackFromLinking = () => {
    exitLinkingMode();
    setAuthMode('selector');
  };

  const handleVKConnectSuccess = (token: string, data: ConnectionTokenData) => {
    setConnectionToken(token);
    setConnectionData(data);
    setAuthMode('connect-confirm');
  };

  /**
   * Токен аккаунта, к которому подключаем второй провайдер.
   * Нужен и ручке connect (иначе шлюз отвечает 2002 «Не передан токен»),
   * и финальному POST /oauth/{provider}/connect.
   */
  const resolveLinkingBaseToken = (): string | null => {
    const primary = linkingPrimary ?? readLinkingPrimary();
    if (primary) {
      const token = storageGet(TOKEN_KEYS[primary]);
      if (token) return token;
    }
    return state.token || findAnyStoredToken();
  };

  /** Связывание завершено (или брошено) — дальше вкладка ведёт себя как обычный вход. */
  const exitLinkingMode = () => {
    sessionRemove(LINKING_MODE_KEY);
    sessionRemove(LINKING_PRIMARY_KEY);
    clearLinkingHints();
    setLinkingPrimary(null);
  };

  /**
   * Сервис назвал свой аккаунт (`uid`) и что к нему подключить (`connect`) — не заставляем
   * выбирать главный аккаунт и жать «Подключить» второй раз, сразу ведём к провайдеру.
   * Главный аккаунт берём ТОЛЬКО из своих сессий: uid не секрет, и чужая страница с ним
   * может разве что подключить человеку его же соцсеть к его же аккаунту. Сессии с таким
   * uid нет — обычная страница связывания (войти там, и подсказка сработает после входа).
   * true = ушли к провайдеру.
   */
  const followLinkingHints = async (tokensData: StoredTokens | null): Promise<boolean> => {
    const uid = sessionGet(LINKING_UID_KEY);
    if (!uid || !tokensData) return false;

    let tokens = tokensData;
    let primary = findPlatformByUid(tokens, uid);
    // Нужная страница ВК есть среди сохранённых, но активна другая — переключаемся на неё.
    if (!primary && listVkAccounts().some((acc) => acc.uid === uid)) {
      setActiveVkAccount(uid);
      setVkAccounts(listVkAccounts());
      const refreshed = await checkStoredTokens();
      if (refreshed) {
        tokens = refreshed;
        setStoredTokensData(refreshed);
      }
      primary = findPlatformByUid(tokens, uid);
    }
    if (!primary) return false;

    handleSelectPrimaryAccount(primary);

    // Подсказка одноразовая: «Назад» от провайдера должно вернуть на страницу связывания,
    // а не снова к тому же провайдеру.
    const connect = sessionGet(LINKING_CONNECT_KEY) as LinkableProvider | null;
    sessionRemove(LINKING_CONNECT_KEY);
    if (!connect || !LINKABLE.includes(connect) || connect === primary) return false;
    if (tokens[primary]?.backendUser?.connection?.[connect]) return false;

    handleConnectProviderFromLinking(connect);
    return true;
  };

  /**
   * Если вкладка в режиме связывания — возвращаемся на страницу связывания
   * с обновлёнными токенами. true = обработали, вызывающему делать нечего.
   */
  const returnToLinkingIfNeeded = async (): Promise<boolean> => {
    if (sessionGet(LINKING_MODE_KEY) !== 'true') return false;

    const tokensData = await checkStoredTokens();
    if (tokensData) {
      setStoredTokensData(tokensData);
    }
    if (await followLinkingHints(tokensData)) return true;
    setAuthMode('account-linking');
    return true;
  };

  // Обработчик успешной авторизации в режиме связывания
  const handleAuthSuccessFromLinking = async (platform: AuthPlatform) => {
    authLogger.info('Авторизация завершена в режиме связывания', { platform });
    // Разбор режима связывания живёт внутри handleAuthSuccess: после редиректа
    // на провайдера мы возвращаемся уже в обычный authMode ('vk'), и проверка
    // обязана быть именно там, а не только в этой ветке.
    await handleAuthSuccess(platform);
  };

  const handleBackToSelector = async () => {
    if (await returnToLinkingIfNeeded()) return;
    setAuthMode('selector');
  };

  const handleTelegramConnectSuccess = (token: string, data: ConnectionTokenData) => {
    setConnectionToken(token);
    setConnectionData(data);
    // Запоминаем uid, который нужно будет удалить из localStorage

    setAuthMode('connect-confirm');
  };

  const handleConnectConfirm = async () => {
    if (!connectionData || !connectionToken) return;

    try {
      setConnectError(null); // Очищаем предыдущие ошибки

      // Связывание идёт «от» главного аккаунта: именно его токен уходит в
      // заголовке, к нему прицепляется второй провайдер. Без этого connectAccount
      // брал первый попавшийся токен (а это всегда ВК) и цеплял аккаунт не туда.
      const primary = linkingPrimary ?? readLinkingPrimary();
      const primaryToken = primary && primary !== connectionData.provider
        ? storageGet(TOKEN_KEYS[primary])
        : null;

      authLogger.info('Подтверждение связывания', {
        provider: connectionData.provider,
        primary,
        usePrimaryToken: Boolean(primaryToken),
      });

      const result = await connectAccount(connectionData.provider, connectionToken, primaryToken);
      if (result.result.result) {
        // Успешно подключили аккаунт
        
        // Если аккаунты объединены, оставляем только аккаунт с uid из ответа
        if (result.result.status === 'merged') {
          const resultUid = result.uid;
          
          // Проверяем текущие данные в localStorage
          const vkCache = storageGet('ssmm-vk-user-cache');
          const telegramCache = storageGet('ssmm-telegram-user-cache');
          
          let shouldRemoveVK = false;
          let shouldRemoveTelegram = false;
          
          // Определяем, какие аккаунты нужно удалить
          if (vkCache) {
            try {
              const vkData = JSON.parse(vkCache);
              if (vkData.backendUser?.uid !== resultUid) {
                shouldRemoveVK = true;
              }
            } catch (e) {
              console.error('Error parsing VK cache:', e);
            }
          }
          
          if (telegramCache) {
            try {
              const telegramData = JSON.parse(telegramCache);
              if (telegramData.backendUser?.uid !== resultUid) {
                shouldRemoveTelegram = true;
              }
            } catch (e) {
              console.error('Error parsing Telegram cache:', e);
            }
          }
          
          // Удаляем аккаунты, которые не соответствуют результирующему uid
          if (shouldRemoveVK) {
            storageRemove('ssmm-token-vk');
            storageRemove('ssmm-vk-user-cache');
          }
          
          if (shouldRemoveTelegram) {
            storageRemove('ssmm-token-telegram');
            storageRemove('ssmm-telegram-user-cache');
          }
        }

        // Обновляем данные токенов
        const tokensData = await checkStoredTokens();
        if (tokensData) {
          setStoredTokensData(tokensData);
        }

        // Связывать больше нечего: снимаем режим, чтобы «На главную» увело
        // обратно в сервис по return_url, а не по кругу на страницу связывания.
        exitLinkingMode();

        // Сохраняем результат и показываем экран успеха
        setConnectResult(result);
        setAuthMode('connect-success');
      }
    } catch (error: any) {
      console.dir(error);
      console.error('Ошибка подключения аккаунта:', error);
      
      // Извлекаем сообщение ошибки
      let errorMessage = 'Произошла ошибка при подключении аккаунта';
      
      // Проверяем разные источники сообщения об ошибке
      if (error?.response?.data?.message) {
        // Ошибка от API с полной структурой
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        // Проверяем, не является ли сообщение JSON строкой
        if (error.message.startsWith('{') && error.message.endsWith('}')) {
          try {
            const errorData = JSON.parse(error.message);
            if (errorData?.message) {
              errorMessage = errorData.message;
            }
          } catch {
            // Если не удалось распарсить JSON, используем исходное сообщение
            errorMessage = error.message;
          }
        } else {
          // Обычное текстовое сообщение
          errorMessage = error.message;
        }
      }
      
      setConnectError(errorMessage);
      
      // Отправляем postMessage об ошибке родительскому окну
      if (window.opener || window.parent !== window) {
        const message = {
          type: 'CONNECT_ERROR',
          data: {
            message: errorMessage
          }
        };
        
        if (window.opener) {
          window.opener.postMessage(message, '*');
        }
        
        if (window.parent !== window) {
          window.parent.postMessage(message, '*');
        }
      }
    }
  };

  const handleConnectCancel = () => {
    setConnectionToken('');
    setConnectionData(null);
    setConnectError(null);

    // В режиме связывания отказ от одного подключения — это не отказ от всего
    // флоу: возвращаем на страницу связывания и оставляем окно открытым, чтобы
    // сервис не закрыл popup и не отправил пользователя на второй круг.
    if (sessionGet(LINKING_MODE_KEY) === 'true') {
      setAuthMode('account-linking');
      return;
    }

    setAuthMode('selector');

    // Отправляем postMessage об отмене родительскому окну
    if (window.opener || window.parent !== window) {
      const message = {
        type: 'CONNECT_CANCELLED',
        data: {}
      };
      
      if (window.opener) {
        window.opener.postMessage(message, '*');
      }
      
      if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    }
  };

  const handleGoToMain = async () => {
    // Очищаем состояние подключения
    setConnectionToken('');
    setConnectionData(null);
    setConnectResult(null);
    setConnectError(null);

    // Связывание ещё не доведено до конца — возвращаемся на его страницу.
    // После успешного связывания флаг уже снят, и мы уходим по return_url.
    if (await returnToLinkingIfNeeded()) return;

    // Перенаправляем на главную страницу
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = normalizeReturnUrl(
      urlParams.get('return_url') || sessionGet('ssmm-return-url'),
    );
    
    if (returnUrl) {
      // Если есть return_url, перенаправляем туда
      sessionRemove('ssmm-return-url'); // Очищаем после использования
      window.location.href = returnUrl;
    } else {
      // Иначе возвращаемся к селектору авторизации
      setAuthMode('selector');
    }
  };

  const handleQuickLogin = async (platform: AuthPlatform) => {
    authLogger.info('Быстрый вход', { platform });

    const userData = storedTokensData[platform];
    if (userData && !userData.isExpired) {
      const tokenKey = TOKEN_KEYS[platform];
      const token = storageGet(tokenKey);
      if (token) {
        authLogger.info('Токен найден, выполняется вход', { platform, tokenKey });
        
        // Входим в систему
        login(userData.user, token, userData.backendUser);
        
        // Проверяем, нужно ли делать redirect на мобильных устройствах
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');
        const isMobile = isMobileUserAgent();
        const mode = urlParams.get('mode');
        
        authLogger.info('Определение метода завершения авторизации', { 
          hasReturnUrl: !!returnUrl,
          isMobile,
          returnUrl,
          mode 
        });
        
        // Если режим extension, используем redirectToReturnUrl который добавит токен в URL
        if (mode === 'extension') {
          authLogger.info('Выбран режим: extension - добавление токена в URL');
          redirectToReturnUrl(token);
        } else if (returnUrl && isMobile) {
          authLogger.info('Выбран режим: редирект на return_url');
          // Делаем redirect на return_url с токеном
          redirectToReturnUrl(token);
        } else {
          authLogger.info('Выбран режим: отправка через postMessage');
          // Отправляем токен и полные данные пользователя родительскому окну
          sendToParent({
            type: 'AUTH_SUCCESS',
            data: {
              token,
              user: userData.backendUser, // Отправляем полные данные пользователя с бэкенда
            },
          });
        }
      } else {
        authLogger.error('Токен не найден в localStorage', { platform, tokenKey });
      }
    } else {
      authLogger.warning('Данные пользователя отсутствуют или токен истек', { 
        platform,
        hasUserData: !!userData,
        isExpired: userData?.isExpired 
      });
    }
  };

  const handleClearStoredData = (platform?: AuthPlatform) => {
    if (platform) {
      // Удаляем данные конкретной платформы
      const newStoredData = { ...storedTokensData };
      delete newStoredData[platform];
      setStoredTokensData(newStoredData);
      clearStoredData(platform);
    } else {
      // Очищаем все данные
      setStoredTokensData({});
      clearStoredData();
    }
  };

  const handleAuthSuccess = async (platform: AuthPlatform) => {
    authLogger.success('Авторизация успешно завершена', { platform });

    // Вход мог быть шагом связывания аккаунтов: тогда завершать авторизацию и
    // уходить по return_url нельзя — сервис снова пришлёт нас связываться.
    if (await returnToLinkingIfNeeded()) {
      authLogger.info('Возврат на страницу связывания после входа', { platform });
      return;
    }

    // После успешной авторизации проверяем токены
    const tokensData = await checkStoredTokens();
    if (tokensData) {
      authLogger.info('Токены проверены', { 
        hasVK: !!tokensData.vk,
        hasTelegram: !!tokensData.telegram,
        hasMax: !!tokensData.max,
        hasYandex: !!tokensData.yandex,
        hasEmail: !!tokensData.email,
        vkExpired: tokensData.vk?.isExpired,
        telegramExpired: tokensData.telegram?.isExpired,
        yandexExpired: tokensData.yandex?.isExpired,
        emailExpired: tokensData.email?.isExpired
      });
      
      setStoredTokensData(tokensData);
      
      // Используем платформу, через которую пользователь авторизовался
      const userData = tokensData[platform];
      
      if (userData && !userData.isExpired) {
        const tokenKey = TOKEN_KEYS[platform];
        const token = storageGet(tokenKey);
        if (token) {
          authLogger.info('Токен найден, выполняется вход', { platform });
          
          // Входим в систему
          login(userData.user, token, userData.backendUser);
          
          // Проверяем, нужно ли делать redirect на мобильных устройствах
          const urlParams = new URLSearchParams(window.location.search);
          const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');
          const isMobile = isMobileUserAgent();
          const mode = urlParams.get('mode');
          
          authLogger.info('Определение метода завершения авторизации', { 
            hasReturnUrl: !!returnUrl,
            isMobile,
            returnUrl,
            mode 
          });
          
          // Если режим extension, используем redirectToReturnUrl который добавит токен в URL
          if (mode === 'extension') {
            authLogger.info('Выбран режим: extension - добавление токена в URL');
            redirectToReturnUrl(token);
          } else if (returnUrl && isMobile) {
            authLogger.info('Выбран режим: редирект на return_url');
            redirectToReturnUrl(token);
          } else {
            authLogger.info('Выбран режим: отправка через postMessage');
            sendToParent({
              type: 'AUTH_SUCCESS',
              data: {
                token,
                user: userData.backendUser,
              },
            });
          }
        } else {
          authLogger.error('Токен не найден в localStorage', { platform, tokenKey });
        }
      } else {
        authLogger.warning('Данные пользователя отсутствуют или токен истек', { platform });
      }
    } else {
      authLogger.error('Не удалось получить данные токенов после авторизации');
    }
  };

  // Отображаем результат авторизации
  if (state.isAuthenticated && state.user) {
    const hasParentWindow = Boolean(window.opener) || window.parent !== window;
    const skin = getSkin();
    const tokenStorageKey = TOKEN_KEYS[state.user.platform];
    // state.token и ключ платформы; запасные ключи — если рассинхрон кэша и platform
    const authToken =
      state.token ||
      storageGet(tokenStorageKey) ||
      storageGet(TOKEN_KEYS.vk) ||
      storageGet(TOKEN_KEYS.telegram) ||
      storageGet(TOKEN_KEYS.max) ||
      storageGet(TOKEN_KEYS.yandex) ||
      storageGet(TOKEN_KEYS.email) ||
      '';

    type BuildAppAuthUrlOpts = {
      /** vk-boss: Next собран с trailingSlash: true — без лишнего 302 /auth → /auth/ (риск для query у CDN) */
      trailingSlashAuth?: boolean;
      /** Путь после входа (SSOCallbackHandler на стороне приложения) */
      postLoginPath?: string;
    };

    const buildAuthUrl = (baseUrl: string, opts?: BuildAppAuthUrlOpts) => {
      const origin = baseUrl.replace(/\/+$/, '');
      const authPath = opts?.trailingSlashAuth ? '/auth/' : '/auth';
      const entry = `${origin}${authPath}`;
      const params = new URLSearchParams();
      if (authToken) {
        params.set('token', authToken);
      }
      params.set('sso_source', 'ssmm');
      params.set('return_url', `${origin}${authPath}`);
      if (
        opts?.postLoginPath &&
        opts.postLoginPath.startsWith('/') &&
        !opts.postLoginPath.startsWith('//')
      ) {
        params.set('post_login_redirect', opts.postLoginPath);
      }
      const query = params.toString();
      return query ? `${entry}?${query}` : entry;
    };

    const multiChatAuthUrl = buildAuthUrl('https://app.multi-chat.com.ru');
    const telegramBossAuthUrl = buildAuthUrl('https://app.telegram-boss.ru');
    const vkBossAuthUrl = buildAuthUrl('https://vk-boss.ru', {
      trailingSlashAuth: true,
      postLoginPath: '/dashboard/tariffs',
    });
    const canOpenAppLinks = Boolean(authToken);

    return (
      <div className="flex flex-col min-h-screen bg-zinc-900">
        <div className="flex-1 flex flex-col justify-center px-6 py-8">
          {/* Success icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          <h2 className="text-xl font-semibold text-white text-center mb-2">
            Авторизация успешна
          </h2>
          <p className="text-zinc-400 text-center mb-6">
            {state.user.name}
          </p>
          
          {/* User info */}
          <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-zinc-500">Платформа</span>
              <span className="text-sm text-zinc-300">
                {PLATFORM_LABELS[state.user.platform]}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-zinc-500">ID</span>
              <span className="text-sm text-zinc-300 font-mono">{state.user.id}</span>
            </div>
          </div>
          
          {/* Auto close notice. Без окна-родителя со скином закрываться нечему — там кнопка возврата. */}
          {!(skin && !hasParentWindow) && (
            <div className="mt-6 p-3 rounded-xl bg-zinc-900 border border-zinc-800">
              <p className="text-xs text-zinc-500 text-center">
                Окно закроется автоматически
              </p>
            </div>
          )}

          {/* Fallback если родительское окно недоступно */}
          {skin && !hasParentWindow ? (
            // Сервис со скином: чужие приложения не предлагаем, только вернуться к себе.
            <a
              href={canOpenAppLinks ? skinLoginUrl(skin, authToken) : skinOrigin(skin)}
              className="block mt-6 w-full rounded-xl bg-[#3079e0] px-4 py-3 text-center text-sm font-medium text-on-accent hover:bg-[#1d62c3] transition-colors"
            >
              Вернуться в {skin.appName}
            </a>
          ) : hasParentWindow ? (
            <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400/80 text-center">
                Если окно не закрылось — откройте через приложение или обновите родительскую страницу
              </p>
            </div>
          ) : (
            <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-300 text-center">
                Выберите приложение:
              </p>
              {!canOpenAppLinks && (
                <p className="mt-2 text-xs text-red-400/90 text-center">
                  Токен не найден — обновите страницу SSO или войдите снова.
                </p>
              )}
              <div className="mt-3 space-y-2">
                <a
                  href={canOpenAppLinks ? multiChatAuthUrl : undefined}
                  aria-disabled={!canOpenAppLinks}
                  className={`block w-full rounded-lg border border-amber-400/30 bg-zinc-900/70 px-3 py-2.5 text-center text-sm font-medium text-amber-100 hover:bg-zinc-800 transition-colors ${!canOpenAppLinks ? 'pointer-events-none opacity-45' : ''}`}
                >
                  Мультичат - перейти
                </a>
                <a
                  href={canOpenAppLinks ? telegramBossAuthUrl : undefined}
                  aria-disabled={!canOpenAppLinks}
                  className={`block w-full rounded-lg border border-amber-400/30 bg-zinc-900/70 px-3 py-2.5 text-center text-sm font-medium text-amber-100 hover:bg-zinc-800 transition-colors ${!canOpenAppLinks ? 'pointer-events-none opacity-45' : ''}`}
                >
                  Телеграм босс - перейти
                </a>
                <a
                  href={canOpenAppLinks ? vkBossAuthUrl : undefined}
                  aria-disabled={!canOpenAppLinks}
                  className={`block w-full rounded-lg border border-amber-400/30 bg-zinc-900/70 px-3 py-2.5 text-center text-sm font-medium text-amber-100 hover:bg-zinc-800 transition-colors ${!canOpenAppLinks ? 'pointer-events-none opacity-45' : ''}`}
                >
                  ВК босс - перейти
                </a>
              </div>
            </div>
          )}
        </div>
        
        <DebugLogger autoShow={false} />
      </div>
    );
  }

  const renderAuthComponent = () => {
    switch (authMode) {
      case 'vk':
        return (
          <VKAuth
            appId={import.meta.env.VITE_VK_APP_ID || '51523045'}
            onCancel={handleBackToSelector}
            onSuccess={() => handleAuthSuccess('vk')}
            newAccount={vkNewAccount}
          />
        );
      case 'telegram':
        return (
          <TelegramAuth 
            botName={import.meta.env.VITE_TELEGRAM_BOT_NAME || ''}
            onCancel={handleBackToSelector}
            onSuccess={() => handleAuthSuccess('telegram')}
          />
        );
      case 'max':
        return (
          <MaxAuth
            onCancel={handleBackToSelector}
            onSuccess={() => handleAuthSuccess('max')}
          />
        );
      case 'yandex':
        return (
          <YandexAuth
            onCancel={handleBackFromYandex}
            onSuccess={() => handleAuthSuccess('yandex')}
          />
        );
      case 'email':
        return (
          <EmailAuth
            onCancel={handleBackToSelector}
            onSelectYandex={handleSelectYandexFromMail}
            onSuccess={() => handleAuthSuccess('email')}
          />
        );
      case 'telegram-connect':
        return (
          <TelegramConnect
            botName={import.meta.env.VITE_TELEGRAM_BOT_NAME || ''}
            sessionToken={resolveLinkingBaseToken()}
            onSuccess={handleTelegramConnectSuccess}
            onCancel={handleBackToSelector}
          />
        );
      case 'vk-connect':
        return (
          <VKConnect
            appId={import.meta.env.VITE_VK_APP_ID || '51523045'}
            sessionToken={resolveLinkingBaseToken()}
            onSuccess={handleVKConnectSuccess}
            onCancel={handleBackToSelector}
          />
        );
      case 'account-linking':
        return (
          <AccountLinkingPage
            storedTokensData={storedTokensData}
            primary={linkingPrimary}
            onSelectPrimary={handleSelectPrimaryAccount}
            onConnectProvider={handleConnectProviderFromLinking}
            onBack={handleBackFromLinking}
            onLoginProvider={handleLoginProviderFromLinking}
            isLoading={state.isLoading}
          />
        );
      case 'account-linking-login-vk':
        return (
          <VKAuth 
            appId={import.meta.env.VITE_VK_APP_ID || '51523045'}
            onCancel={() => setAuthMode('account-linking')}
            onSuccess={() => handleAuthSuccessFromLinking('vk')}
          />
        );
      case 'account-linking-login-telegram':
        return (
          <TelegramAuth 
            botName={import.meta.env.VITE_TELEGRAM_BOT_NAME || ''}
            onCancel={() => setAuthMode('account-linking')}
            onSuccess={() => handleAuthSuccessFromLinking('telegram')}
          />
        );
      case 'account-linking-login-max':
        return (
          <MaxAuth
            onCancel={() => setAuthMode('account-linking')}
            onSuccess={() => handleAuthSuccessFromLinking('max')}
          />
        );
      case 'account-linking-login-email':
        return (
          <EmailAuth
            onCancel={() => setAuthMode('account-linking')}
            onSelectYandex={handleSelectYandexFromMail}
            onSuccess={() => handleAuthSuccessFromLinking('email')}
          />
        );
      case 'connect-confirm':
        return connectionData ? (
          <ConnectAccountConfirm
            connectionData={connectionData}
            onConfirm={handleConnectConfirm}
            onCancel={handleConnectCancel}
            isLoading={state.isLoading}
            error={connectError}
          />
        ) : null;
      case 'connect-success':
        return connectResult ? (
          <ConnectSuccess
            connectResult={connectResult}
            onGoToMain={handleGoToMain}
          />
        ) : null;
      default:
        return (
          <AuthSelector
            onSelectVK={handleSelectVK}
            onSelectTelegram={handleSelectTelegram}
            onSelectMax={handleSelectMax}
            onSelectYandex={handleSelectYandex}
            onSelectEmail={handleSelectEmail}
            storedTokensData={storedTokensData}
            isVerifyingSessions={sessionsVerifying}
            onQuickLogin={handleQuickLogin}
            onClearStoredData={handleClearStoredData}
            onConnectTelegram={handleConnectTelegram}
            vkAccounts={vkAccounts}
            onAddVkAccount={handleAddVkAccount}
            onSwitchVkAccount={handleSwitchVkAccount}
            onRemoveVkAccount={handleRemoveVkAccount}
          />
        );
    }
  };

  return (
    <div className="min-h-screen">
      {renderAuthComponent()}
    </div>
  );
};

export default App; 